import React, { useState } from 'react';
import { 
  Database, 
  LayoutTemplate, 
  Settings2, 
  Printer, 
  Ruler, 
  Menu, 
  X, 
  Link2, 
  ChevronDown, 
  ChevronRight, 
  Type, 
  Barcode, 
  DollarSign, 
  Hash, 
  QrCode, 
  Image as ImageIcon, 
  Square,
  FolderOpen,
  UserCheck,
  RotateCcw,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  Compass,
  Linkedin
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useTour } from '../../store/TourContext';
import { Unit } from '../../types';
import { macSpring } from '../../utils/macAnimations';
import { AppLogo } from './AppLogo';
import { LogoShowcaseModal } from './LogoShowcaseModal';

interface SidebarProps {
  activeStep: 'data' | 'design' | 'imposition' | 'production';
  setActiveStep: (step: 'data' | 'design' | 'imposition' | 'production') => void;
}

export const SaaSSidebar = ({ activeStep, setActiveStep }: SidebarProps) => {
  const { 
    appUnit, 
    setAppUnit, 
    setShowSettings, 
    setShowAuthDashboard,
    setShowLocalAuthModal,
    user,
    startFreshProject,
    csvData, 
    setCsvData, 
    template, 
    setTemplate 
  } = useSaaSContext();
  const { startTour } = useTour();
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDataMappingExpanded, setIsDataMappingExpanded] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('vdp_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('vdp_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const steps = [
    { id: 'data', label: '1. Data Source', icon: Database },
    { id: 'design', label: '2. Template Builder', icon: LayoutTemplate },
    { id: 'imposition', label: '3. Imposition Layout', icon: Settings2 },
    { id: 'production', label: '4. Production Export', icon: Printer },
  ] as const;

  const handleStepClick = (stepId: 'data' | 'design' | 'imposition' | 'production') => {
    setActiveStep(stepId);
    setMobileMenuOpen(false);
  };

  const handleMapElementColumn = (elId: string, colName: string) => {
    setTemplate({
      ...template,
      elements: template.elements.map(e => {
        if (e.id === elId) {
          const fallbackVal = e.content || e.text || e.staticValue || e.name || 'Sample Text';
          return {
            ...e,
            columnName: colName || undefined,
            isStatic: !colName,
            content: e.content || fallbackVal,
            text: e.text || fallbackVal,
            staticValue: e.staticValue || fallbackVal
          };
        }
        return e;
      })
    });
  };

  const handleToggleHideIfEmpty = (elId: string, checked: boolean) => {
    setTemplate({
      ...template,
      elements: template.elements.map(e => {
        if (e.id === elId) {
          return {
            ...e,
            hideIfEmpty: checked
          };
        }
        return e;
      })
    });
  };

  const handleAutoMapAll = () => {
    if (!csvData) return;
    const headersLower = csvData.headers.map(h => ({ raw: h, lower: h.toLowerCase().replace(/[^a-z0-9]/g, '') }));

    setTemplate({
      ...template,
      elements: template.elements.map(el => {
        const nameTarget = (el.name || el.columnName || el.text || el.type || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const matched = headersLower.find(h => nameTarget.includes(h.lower) || h.lower.includes(nameTarget));
        if (matched) {
          return { ...el, columnName: matched.raw, isStatic: false };
        }
        return el;
      })
    });
  };

  const handleLoadSampleData = () => {
    setCsvData({
      headers: ['SKU', 'Product_Name', 'Price', 'Barcode', 'Serial_No', 'Category'],
      rows: [
        { SKU: 'PROD-101', Product_Name: 'Wireless Headphones', Price: '79.99', Barcode: '8901234567891', Serial_No: 'SN-9012', Category: 'Electronics' },
        { SKU: 'PROD-102', Product_Name: 'Memory Foam Cushion', Price: '34.50', Barcode: '8901234567907', Serial_No: 'SN-9013', Category: 'Office' },
        { SKU: 'PROD-103', Product_Name: 'Thermal Tumbler', Price: '24.95', Barcode: '8901234567914', Serial_No: 'SN-9014', Category: 'Home' },
      ]
    });
  };

  const getElementIcon = (type: string) => {
    switch (type) {
      case 'text':
      case 'uppercase':
        return <Type className="w-3.5 h-3.5 text-blue-400" />;
      case 'barcode':
      case 'ean13':
      case 'upca':
      case 'datamatrix':
      case 'pdf417':
        return <Barcode className="w-3.5 h-3.5 text-amber-400" />;
      case 'currency':
        return <DollarSign className="w-3.5 h-3.5 text-emerald-400" />;
      case 'serial':
        return <Hash className="w-3.5 h-3.5 text-purple-400" />;
      case 'qr':
      case 'aztec':
        return <QrCode className="w-3.5 h-3.5 text-indigo-400" />;
      case 'image':
        return <ImageIcon className="w-3.5 h-3.5 text-rose-400" />;
      default:
        return <Square className="w-3.5 h-3.5 text-zinc-400" />;
    }
  };

  const renderNavContent = (collapsed = false, isMobile = false) => {
    if (collapsed && !isMobile) {
      return (
        <div className="flex flex-col h-full items-center justify-between py-2">
          {/* Top Logo & Expand Button */}
          <div className="w-full flex flex-col items-center">
            <div className="h-14 w-full flex items-center justify-center border-b border-zinc-800">
              <button
                onClick={toggleCollapse}
                className="w-9 h-9 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-400 hover:text-white rounded-lg flex items-center justify-center transition-all cursor-pointer group"
                title="Expand Sidebar"
                id="btn-expand-sidebar-top"
              >
                <PanelLeftOpen className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" />
              </button>
            </div>

            {/* Collapsed Steps Icons */}
            <div className="w-full flex flex-col items-center gap-2 mt-3 px-2">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                const isActive = activeStep === step.id;
                return (
                  <button
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    title={step.label}
                    id={`btn-step-collapsed-${step.id}`}
                    className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center transition-all cursor-pointer relative group ${
                      isActive
                        ? 'bg-blue-600/20 text-blue-400 font-semibold border border-blue-500/40 shadow-sm'
                        : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-blue-400' : 'text-zinc-400 group-hover:text-zinc-200'}`} />
                    <span className="text-[8px] font-mono leading-none mt-0.5 opacity-60 group-hover:opacity-100">{idx + 1}</span>
                  </button>
                );
              })}
            </div>

            {/* Collapsed Data Mapping Shortcut */}
            <div className="w-full pt-3 mt-3 border-t border-zinc-800/80 flex flex-col items-center">
              <button
                onClick={() => {
                  setIsCollapsed(false);
                  setIsDataMappingExpanded(true);
                }}
                title={`Data Mapping System ${csvData ? `(${template.elements.filter(e => e.columnName).length}/${template.elements.length} mapped)` : '(No CSV loaded)'}`}
                id="btn-data-mapping-collapsed"
                className="w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:text-blue-400 hover:bg-zinc-900 transition-colors cursor-pointer relative group"
              >
                <Link2 className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" />
                {csvData && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full ring-2 ring-zinc-950" />
                )}
              </button>
            </div>

            {/* Collapsed Unit Selector */}
            <div className="w-full pt-3 mt-3 border-t border-zinc-800/80 flex flex-col items-center">
              <button
                onClick={() => {
                  const units: Unit[] = ['mm', 'cm', 'in', 'pt'];
                  const nextIdx = (units.indexOf(appUnit) + 1) % units.length;
                  setAppUnit(units[nextIdx]);
                }}
                title={`Current Unit: ${appUnit} (Click to cycle mm/cm/in/pt)`}
                id="btn-unit-collapsed"
                className="w-10 h-10 flex flex-col items-center justify-center rounded-lg bg-zinc-900 border border-zinc-800 hover:border-blue-500/50 text-zinc-300 transition-all cursor-pointer font-mono group"
              >
                <Ruler className="w-3.5 h-3.5 text-zinc-400 group-hover:text-blue-400 transition-colors" />
                <span className="text-[8px] font-bold text-blue-400 uppercase mt-0.5">{appUnit}</span>
              </button>
            </div>
          </div>

          {/* Collapsed Footer Icons */}
          <div className="w-full flex flex-col items-center gap-2 pt-3 border-t border-zinc-800 px-2">
            <button
              onClick={() => startTour(0)}
              title="Take Guided Product Tour"
              className="w-10 h-10 flex items-center justify-center rounded-lg text-indigo-400 hover:text-white hover:bg-indigo-950/60 transition-colors cursor-pointer group"
              id="btn-tour-collapsed"
            >
              <Compass className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform" />
            </button>

            <button
              onClick={() => setShowAuthDashboard(true)}
              title="Saved Templates & Cloud Library"
              className="w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors cursor-pointer"
              id="btn-saved-dashboard-collapsed"
            >
              <FolderOpen className="w-4 h-4 text-blue-400" />
            </button>

            <button
              onClick={() => setShowSettings(true)}
              title="Workspace Settings"
              className="w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors cursor-pointer"
              id="btn-settings-collapsed"
            >
              <Settings2 className="w-4 h-4 text-zinc-400 hover:text-zinc-200" />
            </button>

            {user ? (
              <button
                onClick={() => setShowAuthDashboard(true)}
                title={`User: ${user.name} (${user.email})`}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:ring-2 hover:ring-blue-500/50 transition-all cursor-pointer"
              >
                <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full border border-zinc-700" />
              </button>
            ) : (
              <button
                onClick={() => setShowAuthDashboard(true)}
                title="Sign in with Google"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 transition-all cursor-pointer"
                id="btn-signin-collapsed"
              >
                <UserCheck className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={toggleCollapse}
              title="Expand Sidebar"
              id="btn-expand-sidebar-bottom"
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors cursor-pointer mt-1"
            >
              <PanelLeftOpen className="w-4 h-4 text-blue-400" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Header with Title & Collapse Button */}
        <div className="h-14 flex items-center justify-between px-3.5 border-b border-zinc-800">
          <button
            onClick={() => setShowLogoModal(true)}
            className="flex items-center gap-2.5 min-w-0 text-left cursor-pointer group p-1 -ml-1 rounded-lg hover:bg-zinc-850 transition-colors"
            title="Click to view & download Brand Logo"
          >
            <AppLogo size={32} animated={true} />
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-xs tracking-wide text-zinc-100 group-hover:text-blue-400 transition-colors truncate">
                Variable Sticker
              </span>
              <span className="text-[9px] text-zinc-400 font-mono">VDP Studio</span>
            </div>
          </button>
          
          <div className="flex items-center gap-1">
            {/* Desktop Collapse Sidebar Button */}
            {!isMobile && (
              <button 
                onClick={toggleCollapse}
                className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-lg transition-colors cursor-pointer"
                title="Collapse Sidebar"
                id="btn-collapse-sidebar-header"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            )}

            {/* Mobile close button */}
            {isMobile && (
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-white rounded-md bg-zinc-900 border border-zinc-800"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
        
        <div className="flex-1 p-3 flex flex-col gap-1 mt-2 overflow-y-auto" id="tour-sidebar-steps">
          <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 px-2">Workspace Steps</div>
          {steps.map(step => {
            const Icon = step.icon;
            const isActive = activeStep === step.id;
            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(step.id)}
                id={`btn-step-${step.id}`}
                className={`flex items-center w-full px-3 py-2.5 rounded-lg text-left transition-colors text-sm cursor-pointer ${
                  isActive 
                    ? 'bg-blue-600/20 text-blue-400 font-semibold border border-blue-500/30' 
                    : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'
                }`}
              >
                <Icon className={`w-4 h-4 mr-3 ${isActive ? 'text-blue-400' : 'text-zinc-500'}`} />
                <span className="flex-1">{step.label}</span>
              </button>
            );
          })}

          {/* Data Mapping System Section */}
          <div className="mt-4 px-1 pt-2 border-t border-zinc-800/80">
            <div 
              onClick={() => setIsDataMappingExpanded(!isDataMappingExpanded)}
              className="flex items-center justify-between cursor-pointer group mb-2 select-none"
            >
              <div className="flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest group-hover:text-white transition-colors">
                  Data Mapping System
                </span>
              </div>
              <div className="flex items-center gap-1">
                {csvData && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono border border-blue-500/30">
                    {template.elements.filter(e => e.columnName).length}/{template.elements.length}
                  </span>
                )}
                {isDataMappingExpanded ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
              </div>
            </div>

            {isDataMappingExpanded && (
              <div className="space-y-2 bg-zinc-900/90 border border-zinc-800/80 p-2 rounded-lg text-xs">
                {csvData && csvData.headers.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between text-[10px] text-zinc-400 pb-1.5 border-b border-zinc-800">
                      <span className="font-medium text-zinc-300 truncate max-w-[110px]" title={`${csvData.rows.length} rows, ${csvData.headers.length} headers`}>
                        📊 {csvData.rows.length} Recs ({csvData.headers.length} Cols)
                      </span>
                      <button
                        onClick={handleAutoMapAll}
                        className="text-[9px] bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 px-1.5 py-0.5 rounded font-medium cursor-pointer transition-colors"
                        title="Auto match CSV columns to template elements"
                      >
                        Auto-Map
                      </button>
                    </div>

                    <div className="max-h-52 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                      {template.elements.map((el) => {
                        const icon = getElementIcon(el.type);
                        return (
                          <div key={el.id} className="p-1.5 rounded bg-zinc-950/80 border border-zinc-800/80 space-y-1">
                            <div className="flex items-center justify-between text-[11px]">
                              <span className="font-semibold text-zinc-200 flex items-center gap-1.5 truncate max-w-[130px]" title={el.name || el.columnName || el.text || el.type}>
                                {icon}
                                <span className="truncate">{el.name || el.columnName || el.text || el.type}</span>
                              </span>
                              <span className="text-[9px] text-zinc-500 font-mono uppercase">{el.type}</span>
                            </div>

                            <select
                              className="w-full bg-zinc-900 border border-zinc-700 rounded px-1.5 py-1 text-xs text-zinc-200 outline-none focus:border-blue-500 font-mono cursor-pointer"
                              value={el.columnName || ''}
                              onChange={(e) => handleMapElementColumn(el.id, e.target.value)}
                            >
                              <option value="">-- Static / Unmapped --</option>
                              {csvData.headers.map((col) => (
                                <option key={col} value={col}>
                                  {col}
                                </option>
                              ))}
                            </select>

                            <label className="flex items-center gap-1.5 text-[10px] text-zinc-400 cursor-pointer pt-0.5">
                              <input
                                type="checkbox"
                                checked={el.hideIfEmpty || false}
                                onChange={(e) => handleToggleHideIfEmpty(el.id, e.target.checked)}
                                className="rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-0 w-3 h-3"
                              />
                              <span>Hide if empty</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-2 space-y-2">
                    <p className="text-[11px] text-zinc-400">No CSV Data Source active.</p>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={handleLoadSampleData}
                        className="w-full py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold cursor-pointer shadow-sm transition-colors"
                      >
                        Load Sample Data
                      </button>
                      <button
                        onClick={() => handleStepClick('data')}
                        className="w-full py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded text-[10px] cursor-pointer transition-colors"
                      >
                        Go to Step 1 (Data)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 px-2">
            <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Global Measurement</div>
            <div className="flex items-center justify-between text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 p-2.5 rounded-lg">
              <div className="flex items-center gap-2">
                <Ruler className="w-4 h-4 text-zinc-500" />
                <span className="font-medium text-zinc-300">Unit System</span>
              </div>
              <select
                className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-xs outline-none focus:border-blue-600 text-zinc-200 font-mono"
                value={appUnit}
                onChange={(e) => setAppUnit(e.target.value as Unit)}
              >
                <option value="mm">mm</option>
                <option value="cm">cm</option>
                <option value="in">inch</option>
                <option value="pt">points</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer Area with Explicit Collapse Button */}
        <div className="p-3 border-t border-zinc-800 bg-zinc-950/80 space-y-2">
          {/* Explicit Collapse Sidebar button */}
          {!isMobile && (
            <button
              onClick={toggleCollapse}
              className="flex items-center justify-between w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900 border border-zinc-800/80 transition-colors cursor-pointer"
              title="Collapse Sidebar"
              id="btn-collapse-sidebar"
            >
              <div className="flex items-center gap-2">
                <PanelLeftClose className="w-4 h-4 text-zinc-400" />
                <span>Collapse Sidebar</span>
              </div>
              <span className="text-[10px] font-mono text-zinc-500">Slim</span>
            </button>
          )}

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => {
                setShowAuthDashboard(true);
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white transition-colors cursor-pointer"
            >
              <FolderOpen className="w-3.5 h-3.5 text-blue-400" />
              <span className="font-semibold">Saved Dashboard</span>
            </button>
            <button 
              onClick={() => {
                setShowSettings(true);
                setMobileMenuOpen(false);
              }}
              className="text-[11px] text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
            >
              Settings
            </button>
            <button
              onClick={() => {
                startTour(0);
                setMobileMenuOpen(false);
              }}
              className="text-[11px] text-indigo-400 hover:text-indigo-300 font-semibold flex items-center gap-1 transition-colors cursor-pointer"
              title="Start Guided Product Tour"
            >
              <Compass className="w-3 h-3 text-indigo-400" />
              <span>Tour</span>
            </button>
          </div>
          {user ? (
            <div 
              onClick={() => {
                setShowLocalAuthModal(true);
                setMobileMenuOpen(false);
              }}
              className="p-2 bg-zinc-900 border border-zinc-800 rounded-lg flex items-center gap-2 cursor-pointer hover:border-zinc-700 transition-all"
              title="Click to manage local account session"
            >
              <img src={user.avatar} alt={user.name} className="w-6 h-6 rounded-full" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-zinc-200 truncate">{user.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">{user.email}</div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => {
                setShowLocalAuthModal(true);
                setMobileMenuOpen(false);
              }}
              className="w-full py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs"
            >
              <UserCheck className="w-3.5 h-3.5" /> Local Account Sign In
            </button>
          )}

          {/* Copyright & LinkedIn Profile Badge */}
          <div className="pt-2 border-t border-zinc-800/80 flex items-center justify-between text-[11px] text-zinc-400">
            <span className="text-[10px] text-zinc-500 font-mono truncate">© 2026 VDP Studio</span>
            <a
              href="https://www.linkedin.com/in/rupakdeysample/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-600/15 hover:bg-blue-600/25 text-blue-400 hover:text-blue-300 border border-blue-500/30 transition-all cursor-pointer text-[10px] font-semibold shrink-0"
              title="Connect on LinkedIn"
            >
              <Linkedin className="w-3 h-3 text-blue-400" />
              <span>LinkedIn</span>
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Top Toggle Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-zinc-950 border-b border-zinc-800 z-30 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-zinc-300 hover:text-white bg-zinc-900 border border-zinc-800 rounded-lg cursor-pointer"
            title="Open Workspace Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setShowLogoModal(true)}
            className="flex items-center gap-2 text-left cursor-pointer group"
            title="View & Download Logo"
          >
            <AppLogo size={28} animated={true} />
            <span className="font-bold text-sm text-zinc-100 group-hover:text-blue-400 transition-colors">
              Variable Sticker
            </span>
          </button>
        </div>

        {/* Current Step Pill */}
        <div className="text-xs font-semibold px-2.5 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-blue-400 capitalize">
          {activeStep}
        </div>
      </div>

      {/* Desktop Permanent Sidebar with Collapse Support */}
      <motion.aside 
        initial={false}
        animate={{ width: isCollapsed ? 64 : 256 }}
        transition={macSpring}
        className="hidden lg:flex bg-zinc-950 border-r border-zinc-800 text-zinc-300 flex-col h-full shrink-0 overflow-hidden"
      >
        <div style={{ width: isCollapsed ? 64 : 256 }}>
          {renderNavContent(isCollapsed, false)}
        </div>
      </motion.aside>

      {/* Mobile Backdrop & Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-xs z-50 transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside 
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={macSpring}
              className="lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-zinc-950 border-r border-zinc-800 z-50 overflow-hidden"
            >
              {renderNavContent(false, true)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Logo Showcase & Vector Exporter Modal */}
      <LogoShowcaseModal 
        isOpen={showLogoModal} 
        onClose={() => setShowLogoModal(false)} 
      />
    </>
  );
};

